/**
 * ChatShellSidebar — unified sidebar for the chat-first app shell.
 * Replaces PlatformSidebar and AppDetailSidebar with a single layout:
 * - App switcher button
 * - Primary navigation links
 * - Collapsible agents list
 * - Grouped All Chats history
 * - Footer with user profile
 */

import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar'
import { useApp } from '@/contexts/app-context'
import { useAgents } from '@/hooks/use-agents'
import { usePermissions } from '@/hooks/use-permissions'
import { ChevronDown, Search } from 'lucide-react'
import { AgentIcon } from '@/components/agents/agent-icon'
import { Input } from '@/components/ui/input'
import { AppSwitcherDialog } from './app-switcher-dialog'
import { SearchCommandDialog } from './search-command-dialog'
import { SidebarAllChats } from './sidebar-all-chats'
import { SidebarAgentChats } from './sidebar-agent-chats'
import { SidebarRecentTasks } from './sidebar-recent-tasks'
import { CoworkTaskHistory } from '@/components/cowork/cowork-task-history'
import { isDesktopApp } from '@/lib/desktop'
import { useCoworkStore } from '@/store/cowork-store'

/**
 * ChatShellSidebar content — rendered inside SidebarContent.
 * In CoWork mode the chat list is swapped for the CoWork task history; the app
 * nav + agents stay identical, so the two modes feel consistent.
 */
export function ChatShellSidebar({ coworkMode = false }: { coworkMode?: boolean }) {
  const coworkChatIds = useCoworkStore((s) => s.coworkChatIds)
  const { appId: routeAppId } = useParams<{ appId: string }>()
  const location = useLocation()
  const { app } = useApp()
  const { isAdmin } = usePermissions()
  const [appSwitcherOpen, setAppSwitcherOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [moreAgentsOpen, setMoreAgentsOpen] = useState(false)
  const [agentQuery, setAgentQuery] = useState('')

  const AGENTS_COLLAPSED_COUNT = 4

  // Use route appId first, fall back to last active app (for pages like /app/settings/* that lack appId)
  const appId = useMemo(() => {
    if (routeAppId) {
      try { localStorage.setItem('taqat_last_active_app_id', routeAppId) } catch { /* noop */ }
      return routeAppId
    }
    try {
      return localStorage.getItem('taqat_last_active_app_id')
        || localStorage.getItem('taqat_default_app_id')
        || undefined
    } catch {
      return undefined
    }
  }, [routeAppId])

  const { state: agentsState, loading: agentsLoading } = useAgents({
    autoFetch: true,
    initialParams: appId ? { appId } : {},
  })

  const agents = agentsState.agents || []

  /**
   * Agents shown in the "Show more" popover.
   *
   * With no query it lists exactly the overflow — the ones the collapsed
   * sidebar hid. Once the user types it searches ALL agents, including the few
   * pinned above: someone searching wants the agent, not a reminder that it was
   * already on screen.
   */
  const moreAgents = useMemo(() => {
    const query = agentQuery.trim().toLowerCase()
    if (!query) return agents.slice(AGENTS_COLLAPSED_COUNT)
    return agents.filter((agent) => agent.name.toLowerCase().includes(query))
  }, [agents, agentQuery])
  const basePath = `/app/${appId || ''}`
  const newChatPath = `${basePath}/chat`

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/')

  const getAgentPath = (agentId: string) =>
    `${basePath}/agents/${agentId}`

  const appIconUrl =
    app?.icon && (app.icon.startsWith('http') || app.icon.startsWith('/'))

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') {
        return
      }

      const target = event.target
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT')
      ) {
        return
      }

      event.preventDefault()
      setSearchOpen(true)
    }

    const handleOpenSearch = () => setSearchOpen(true)

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('taqat:open-search', handleOpenSearch)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('taqat:open-search', handleOpenSearch)
    }
  }, [])

  return (
    <>
      {/* App Switcher Button — hidden for now */}
      {false && (
        <SidebarGroup className="py-1 px-3 group-data-[collapsible=icon]:px-2">
          <button
            onClick={() => setAppSwitcherOpen(true)}
            className="flex items-center gap-3 w-full px-2 py-2 rounded-lg hover:bg-sidebar-accent transition-colors text-left group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
          >
            <div className="size-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
              {appIconUrl ? (
                <img src={app!.icon!} alt="" className="size-8 object-cover" />
              ) : (
                <span className="text-sm">{app?.icon || '📱'}</span>
              )}
            </div>
            <div className="min-w-0 flex-1 group-data-[collapsible=icon]:hidden">
              <p className="type-sidebar-title truncate">{app?.name || 'Select App'}</p>
              <p className="type-sidebar-meta truncate text-muted-foreground">
                {app?.description || 'Application'}
              </p>
            </div>
            <ChevronDown className="size-4 text-muted-foreground shrink-0 group-data-[collapsible=icon]:hidden" />
          </button>
        </SidebarGroup>
      )}

      {/*
        Members get a messaging-style rail: a short peek at their Super-Agent
        tasks, then the agent list (every agent, each row showing its latest
        conversation). Both flow into the shell's single scroll — see
        SidebarRecentTasks for why this isn't the admin history component.
        Admins keep the nav-style list + full grouped history below.
      */}
      {!coworkMode && !isAdmin && (
        <>
          <SidebarRecentTasks
            appId={appId}
            onSeeAll={() => setSearchOpen(true)}
            excludeChatIds={isDesktopApp() ? coworkChatIds : []}
          />
          <SidebarAgentChats agents={agents} appId={appId} />
        </>
      )}

      {/* Agents Section - hidden when no agents, and in CoWork mode */}
      {!coworkMode && isAdmin && agents.length > 0 && (
        <SidebarGroup className="py-1">
          <SidebarGroupLabel className="group-data-[collapsible=icon]:hidden flex w-full items-center justify-between px-3 text-muted-foreground">
            <span>Agents</span>
            <span className="type-sidebar-meta text-muted-foreground/70">
              {agents.length}
            </span>
          </SidebarGroupLabel>
          <SidebarGroupContent>
                <SidebarMenu>
                  {agents.slice(0, AGENTS_COLLAPSED_COUNT).map((agent) => {
                    const agentPath = getAgentPath(agent.id)
                    const agentActive = isActive(agentPath)

                    return (
                      <SidebarMenuItem key={agent.id}>
                        <SidebarMenuButton
                          isActive={agentActive}
                          asChild
                        >
                          <Link to={agentPath}>
                            <AgentIcon
                              avatar={agent.avatar}
                              name={agent.name}
                              size="sm"
                              rounded="lg"
                            />
                            <span className="truncate">{agent.name}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  })}
                  {agents.length > AGENTS_COLLAPSED_COUNT && (
                    <SidebarMenuItem>
                      <Popover
                        open={moreAgentsOpen}
                        onOpenChange={(open) => {
                          setMoreAgentsOpen(open)
                          if (!open) setAgentQuery('')
                        }}
                      >
                        <PopoverTrigger asChild>
                          <SidebarMenuButton className="text-muted-foreground">
                            <ChevronDown className="size-4 -rotate-90" />
                            <span>{`Show more (${agents.length - AGENTS_COLLAPSED_COUNT})`}</span>
                          </SidebarMenuButton>
                        </PopoverTrigger>
                        <PopoverContent
                          side="right"
                          align="start"
                          sideOffset={8}
                          className="flex max-h-[60vh] w-64 flex-col p-1"
                        >
                          <div className="relative mb-1">
                            <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              autoFocus
                              value={agentQuery}
                              onChange={(e) => setAgentQuery(e.target.value)}
                              placeholder="Search agents"
                              aria-label="Search agents"
                              className="h-8 pl-7 text-sm"
                            />
                          </div>
                          <div className="flex flex-col overflow-y-auto">
                            {moreAgents.map((agent) => {
                              const agentPath = getAgentPath(agent.id)
                              return (
                                <Link
                                  key={agent.id}
                                  to={agentPath}
                                  onClick={() => setMoreAgentsOpen(false)}
                                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
                                >
                                  <AgentIcon
                                    avatar={agent.avatar}
                                    name={agent.name}
                                    size="sm"
                                    rounded="lg"
                                  />
                                  <span className="truncate">{agent.name}</span>
                                </Link>
                              )
                            })}
                            {moreAgents.length === 0 && (
                              <p className="px-2 py-3 text-center text-sm text-muted-foreground">
                                No agents match “{agentQuery.trim()}”
                              </p>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </SidebarMenuItem>
                  )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      )}

      {/* History — CoWork tasks in CoWork mode; all chats (minus CoWork tasks,
          on desktop) otherwise, so the two lists never overlap. Members don't
          get a history section at all: their latest chat is shown on the agent
          row itself (see SidebarAgentChats). */}
      {coworkMode ? (
        <CoworkTaskHistory appId={appId} />
      ) : (
        isAdmin && (
          <SidebarAllChats
            appId={appId}
            agentIds={agents.map((agent) => agent.id)}
            appAgentsLoading={Boolean(appId) && agentsLoading.loading}
            excludeChatIds={isDesktopApp() ? coworkChatIds : []}
          />
        )
      )}

      {/* App Switcher Dialog */}
      <AppSwitcherDialog
        open={appSwitcherOpen}
        onOpenChange={setAppSwitcherOpen}
        activeAppId={appId}
      />
      <SearchCommandDialog
        open={searchOpen}
        onOpenChange={setSearchOpen}
        newTaskPath={newChatPath}
        appId={appId}
        agents={agents.map((agent) => ({
          id: agent.id,
          name: agent.name,
          avatar: agent.avatar,
        }))}
        agentIds={agents.map((agent) => agent.id)}
        appAgentsLoading={Boolean(appId) && agentsLoading.loading}
      />
    </>
  )
}
